import { useMemo, useState, useEffect } from "react";
import { Area, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, ReferenceLine, ComposedChart, Line } from 'recharts';
import { IdleInfo, IdleProjection } from "../../models/Idle";
import { getTodayAtMidnight, getLastSunday, formatLocalDate } from '../../helpers/dateHelper';
import { getAppliedTheme, getChartThemeColors, getChartSeriesColors, THEME_CHANGE_EVENT, AppTheme } from "../../helpers/themeHelper";

interface IdleProjectionChartProps {
	idleInfo?: IdleInfo[];
	idleProjection?: IdleProjection;
	goal: number;
	userID?: number;
}

/**
 * Renders the idle percentage area chart with projection forecast, goal line, and weekly projected reference.
 */
const IdleProjectionChart = ({ idleInfo, idleProjection, goal, userID }: IdleProjectionChartProps) => {
	const [currentTheme, setCurrentTheme] = useState<AppTheme>(getAppliedTheme());

	useEffect(() => {
		/**
		 * Keeps chart chrome colors in sync when the user toggles light/dark theme.
		 */
		function handleThemeChange(event: Event) {
			const customEvent = event as CustomEvent<AppTheme>;
			if (customEvent.detail === "dark" || customEvent.detail === "light") {
				setCurrentTheme(customEvent.detail);
			}
			else {
				setCurrentTheme(getAppliedTheme());
			}
		}

		window.addEventListener(THEME_CHANGE_EVENT, handleThemeChange);
		return () => {
			window.removeEventListener(THEME_CHANGE_EVENT, handleThemeChange);
		};
	}, []);

	const chartThemeColors = useMemo(() => {
		return getChartThemeColors();
	}, [currentTheme]);

	const chartSeriesColors = useMemo(() => {
		return getChartSeriesColors();
	}, [currentTheme]);

	const chartData = useMemo(() => {
		const idleData = idleInfo || [];
		const dailyProjections = idleProjection?.dailyProjections || [];
		const weekProjection = idleProjection?.projectedIdlePercentage;

		const today = getTodayAtMidnight();

		const lastSunday = getLastSunday();

		const dateMap = new Map<string, {date: string, lastWeek?: number, projected?: number}>();

		for (let dayOffset = 0; dayOffset < 14; dayOffset++) {
			const date = new Date(lastSunday);
			date.setDate(lastSunday.getDate() + dayOffset);
			const dateStr = formatLocalDate(date);
			const dateLabel = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
			dateMap.set(dateStr, { date: dateLabel });
		}

		idleData.forEach(item => {
			const dateStr = item.summaryDate.split('T')[0];
			if (dateMap.has(dateStr)) {
				dateMap.get(dateStr)!.lastWeek = item.idlePercentage;
			}
		});

		const todayStr = formatLocalDate(today);

		const yesterday = new Date(today);
		yesterday.setDate(today.getDate() - 1);
		const yesterdayStr = formatLocalDate(yesterday);

		const yesterdayIdleData = dateMap.get(yesterdayStr);
		const yesterdayHasActualData = yesterdayIdleData && yesterdayIdleData.lastWeek !== undefined && yesterdayIdleData.lastWeek !== null;

		dailyProjections.forEach(proj => {
			const dateStr = proj.date.split('T')[0];

			if (dateStr >= todayStr && dateMap.has(dateStr) && proj.projectedIdlePercentage !== null && proj.projectedIdlePercentage !== undefined) {
				dateMap.get(dateStr)!.projected = proj.projectedIdlePercentage;
			}
		});

		if (yesterdayHasActualData && dateMap.has(yesterdayStr) && !dateMap.get(yesterdayStr)!.projected) {
			dateMap.get(yesterdayStr)!.projected = yesterdayIdleData!.lastWeek;
		}

		const data = Array.from(dateMap.entries())
			.sort((entryA, entryB) => entryA[0].localeCompare(entryB[0]))
			.map(entry => entry[1]);

		const allValues = data.map(point => point.lastWeek).filter(value => value !== undefined && value !== null);
		const allProjections = data.map(point => point.projected).filter(value => value !== undefined && value !== null);
		const dataMax = allValues.length > 0 ? Math.max(...allValues) : 0;
		const projectionMax = allProjections.length > 0 ? Math.max(...allProjections) : 0;
		const maxValue = Math.max(dataMax, projectionMax, goal, weekProjection || 0);
		const yAxisMax = Math.ceil(maxValue / 5) * 5;

		let displayGoal = goal;
		let displayWeekProjection = weekProjection;
		if (weekProjection && Math.abs(weekProjection - goal) < 1.0) {
			const diff = Math.abs(weekProjection - goal);
			const offset = (0.3 - diff) / 2;

			if (goal > weekProjection) {
				displayGoal = goal + offset;
				displayWeekProjection = weekProjection - offset;
			} else {
				displayGoal = goal - offset;
				displayWeekProjection = weekProjection + offset;
			}
		}

		return { data, yAxisMax, weekProjection, displayGoal, displayWeekProjection };
	}, [idleInfo, idleProjection, goal]);

	return (
		<div className="row" key={`idle-chart-${userID}`}>
			<div className="col-12">
				<div className="card">
					<div className="card-body" style={{padding: "0.35rem"}}>
						<h5 className="card-title mb-2 text-center">Idle Time (%)</h5>
						<div style={{ width: '100%', height: 280 }} key={`chart-container-${userID}`}>
							<ResponsiveContainer width="100%" height="100%">
								<ComposedChart
									data={chartData.data}
									key={`composed-chart-${userID}`}
									margin={{
										top: 5,
										right: 10,
										left: -10,
										bottom: 0,
									}}
								>
									<defs>
										<linearGradient id="blueGradient" x1="0" y1="0" x2="0" y2="1">
											<stop offset="0%" stopColor={chartSeriesColors.primaryFillStart} stopOpacity={0.8}/>
											<stop offset="100%" stopColor={chartSeriesColors.primaryFillStart} stopOpacity={0}/>
										</linearGradient>
										<linearGradient id="orangeGradient" x1="0" y1="0" x2="0" y2="1">
											<stop offset="0%" stopColor="#f39c12" stopOpacity={0.6}/>
											<stop offset="100%" stopColor="#f39c12" stopOpacity={0}/>
										</linearGradient>
									</defs>
									<CartesianGrid strokeDasharray="3 3" stroke={chartThemeColors.grid} />
									<XAxis
										dataKey="date"
										stroke={chartThemeColors.axis}
										fontSize={12}
										tick={{ fill: chartThemeColors.axis }}
										tickLine={false}
										tickSize={0}
										tickMargin={0}
										interval={0}
										angle={-45}
										textAnchor="end"
										height={60}
										axisLine={false}
										allowDuplicatedCategory={false}
									/>
									<YAxis
										stroke={chartThemeColors.axis}
										fontSize={12}
										tick={{ fill: chartThemeColors.axis }}
										domain={[0, chartData.yAxisMax]}
										ticks={Array.from({length: Math.floor(chartData.yAxisMax / 5) + 1}, (_, tickIndex) => tickIndex * 5)}
										interval={0}
										tickLine={{ stroke: chartThemeColors.axis, strokeWidth: 1 }}
									/>
									<Tooltip
										contentStyle={{
											backgroundColor: chartThemeColors.tooltipBackground,
											border: `1px solid ${chartThemeColors.tooltipBorder}`,
											borderRadius: '6px',
											color: chartThemeColors.tooltipText
										}}
										content={({ active, payload }) => {
											if (active && payload && payload.length) {
												let filteredPayload = payload.filter(item =>
													item.name === 'Idle %' || item.name === 'Projected'
												);

												const hasIdlePercent = filteredPayload.some(item => item.name === 'Idle %' && item.value !== null && item.value !== undefined);
												const hasProjected = filteredPayload.some(item => item.name === 'Projected' && item.value !== null && item.value !== undefined);

												if (hasIdlePercent && hasProjected) {
													filteredPayload = filteredPayload.filter(item => item.name !== 'Projected');
												}

												return (
													<div style={{
														backgroundColor: chartThemeColors.tooltipBackground,
														border: `1px solid ${chartThemeColors.tooltipBorder}`,
														borderRadius: '6px',
														padding: '10px'
													}}>
														<p style={{ margin: 0, color: chartThemeColors.tooltipText }}>{payload[0].payload.date}</p>
														{filteredPayload.map((entry, index) => (
															<p key={index} style={{ margin: '5px 0 0 0', color: entry.color }}>
																{entry.name}: {typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value}%
															</p>
														))}
													</div>
												);
											}
											return null;
										}}
									/>
									{chartData.weekProjection && chartData.weekProjection > 0 && (
										<ReferenceLine
											y={chartData.displayWeekProjection}
											stroke={chartSeriesColors.projected}
											strokeWidth={1}
										/>
									)}
									<Line
										type="linear"
										dataKey="projected"
										stroke={chartSeriesColors.projected}
										strokeDasharray="5 5"
										strokeWidth={1}
										fill="none"
										name="Projected"
										connectNulls={false}
										dot={(props) => {
											if (props.value === null || props.value === undefined) {
												return <g key={`projected-dot-${props.index}`} />;
											}

											const today = new Date();
											today.setHours(0, 0, 0, 0);
											const yesterday = new Date(today);
											yesterday.setDate(today.getDate() - 1);
											const yesterdayLabel = yesterday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

											const dataDate = props.payload?.date;
											if (!dataDate) {
												return <g key={`projected-dot-${props.index}`} />;
											}

											if (dataDate === yesterdayLabel) {
												return <g key={`projected-dot-${props.index}`} />;
											}

											return <circle key={`projected-dot-${props.index}`} cx={props.cx} cy={props.cy} r={3} fill={chartSeriesColors.projected} />;
										}}
										activeDot={{ r: 4 }}
									/>
									<Area
										type="linear"
										dataKey="lastWeek"
										stroke={chartSeriesColors.primary}
										strokeWidth={2}
										fill="url(#blueGradient)"
										name="Idle %"
										connectNulls={false}
										dot={(props) => {
											if (props.value === null || props.value === undefined || props.payload?.lastWeek === null || props.payload?.lastWeek === undefined) {
												return <circle key={`lastWeek-dot-${props.index}`} cx={props.cx} cy={props.cy} r={0} fill="none" />;
											}
											return <circle key={`lastWeek-dot-${props.index}`} cx={props.cx} cy={props.cy} r={3} fill={chartSeriesColors.primary} />;
										}}
									/>
									<ReferenceLine
										y={chartData.displayGoal}
										stroke={chartSeriesColors.goal}
										strokeWidth={1}
									/>
								</ComposedChart>
							</ResponsiveContainer>
						</div>
						<div className="d-flex justify-content-center gap-4 mt-2">
							<div className="d-flex align-items-center gap-2">
								<div style={{ width: '30px', height: '1px', backgroundColor: chartSeriesColors.goal }}></div>
								<span className="app-page-text" style={{ fontSize: '0.875rem' }}>Goal: {goal}%</span>
							</div>
							{chartData.weekProjection && chartData.weekProjection > 0 && (
								<div className="d-flex align-items-center gap-2">
									<div style={{ width: '30px', height: '1px', backgroundColor: chartSeriesColors.projected }}></div>
									<span className="app-page-text" style={{ fontSize: '0.875rem' }}>Projected: {chartData.weekProjection.toFixed(1)}%</span>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

export default IdleProjectionChart;
